// File Name: CareerCard.jsx

import React, { useState } from "react";
import { motion } from "framer-motion";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/ui/dialog";

import {
  Briefcase,
  Upload,
  MapPin,
  Clock,
  Award,
} from "lucide-react";

import { toast } from "sonner";
import { api } from "@/lib/site";

export default function CareerCard({ job, index }) {
  const [open, setOpen] = useState(false);

  const [submitting, setSubmitting] =
    useState(false);

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    cover_letter: "",
  });

  const [resume, setResume] = useState(null);

  /* =====================================================
     SUBMIT
  ===================================================== */

  const onSubmit = async (e) => {
    e.preventDefault();

    if (!resume) {
      return toast.error(
        "Please upload your resume."
      );
    }

    const ext = resume.name
      .split(".")
      .pop()
      .toLowerCase();

    if (!["pdf", "doc", "docx"].includes(ext)) {
      return toast.error(
        "PDF / DOC / DOCX only."
      );
    }

    setSubmitting(true);

    try {
      const fd = new FormData();

      fd.append("job_id", job.id);
      fd.append("name", form.name);
      fd.append("email", form.email);
      fd.append("phone", form.phone);

      fd.append(
        "cover_letter",
        form.cover_letter
      );

      fd.append("resume", resume);

      const { data } = await api.post(
        "/career/apply",
        fd,
        {
          headers: {
            "Content-Type":
              "multipart/form-data",
          },
        }
      );

      toast.success(data.message);

      setOpen(false);

      setForm({
        name: "",
        email: "",
        phone: "",
        cover_letter: "",
      });

      setResume(null);
    } catch (err) {
      toast.error(
        err.response?.data?.detail ||
          "Submission failed."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* =====================================================
          CARD
      ===================================================== */}

      <motion.div
        initial={{
          opacity: 0,
          y: 30,
        }}
        whileInView={{
          opacity: 1,
          y: 0,
        }}
        transition={{
          delay: index * 0.08,
          duration: 0.6,
        }}
        viewport={{ once: true }}
        whileHover={{
          y: -10,
        }}
        className="
          relative overflow-hidden
          rounded-[28px]
          border border-purple-200/40
          bg-white/80
          backdrop-blur-xl
          shadow-[0_10px_40px_rgba(120,70,180,0.12)]
          p-5 sm:p-6 md:p-7
          transition-all duration-500
          hover:shadow-[0_20px_60px_rgba(120,70,180,0.22)]

          before:absolute
          before:inset-0
          before:bg-gradient-to-br
          before:from-white/40
          before:to-transparent
          before:pointer-events-none
        "
      >
        {/* GLOW EFFECT */}

        <div className="absolute -top-10 -right-10 w-40 h-40 bg-purple-500/20 rounded-full blur-3xl animate-pulse" />

        <div className="absolute bottom-0 left-0 w-28 h-28 bg-fuchsia-400/10 rounded-full blur-2xl" />

        {/* TOP */}

        <div className="relative z-10 flex items-start justify-between mb-5">
          <div
            className="
              w-12 h-12 sm:w-14 sm:h-14
              rounded-2xl
              bg-gradient-to-br
              from-purple-500
              to-fuchsia-500
              flex items-center justify-center
              shadow-lg shadow-purple-500/25
              shrink-0
            "
          >
            <Briefcase className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
          </div>

          <span
            className="
              px-3 sm:px-4
              py-1.5
              rounded-full
              bg-purple-100
              text-purple-700
              text-[11px] sm:text-xs
              font-semibold
              border border-purple-200
              whitespace-nowrap
            "
          >
            {job.type}
          </span>
        </div>

        {/* TITLE */}

        <h3
          className="
            text-xl sm:text-2xl
            font-bold
            text-slate-900
            mb-3
            tracking-tight
            leading-snug
          "
        >
          {job.title}
        </h3>

        {/* DESCRIPTION */}

        <p
          className="
            text-slate-600
            leading-relaxed
            text-sm
            sm:text-[15px]
            mb-6
          "
        >
          {job.description}
        </p>

        {/* INFO */}

        <div className="space-y-3 sm:space-y-4 mb-7">
          <div className="flex items-center gap-3 text-sm text-slate-600">
            <MapPin className="w-4 h-4 text-purple-600 shrink-0" />
            <span className="break-words">
              {job.location}
            </span>
          </div>

          <div className="flex items-center gap-3 text-sm text-slate-600">
            <Clock className="w-4 h-4 text-purple-600 shrink-0" />
            {job.type}
          </div>

          <div className="flex items-center gap-3 text-sm text-slate-600">
            <Award className="w-4 h-4 text-purple-600 shrink-0" />
            {job.department}
          </div>
        </div>

        {/* BUTTON */}

        <button
          onClick={() => setOpen(true)}
          className="
            w-full py-3.5
            rounded-2xl
            bg-gradient-to-r
            from-purple-600
            via-fuchsia-600
            to-purple-700
            text-white
            font-semibold
            shadow-lg shadow-purple-500/25
            hover:scale-[1.02]
            active:scale-[0.98]
            hover:shadow-purple-500/40
            transition-all duration-300
            text-sm sm:text-base
          "
        >
          Apply Now
        </button>
      </motion.div>

      {/* =====================================================
          MODAL
      ===================================================== */}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="
            w-[95vw]
            sm:max-w-2xl
            border-0
            rounded-[28px]
            sm:rounded-[32px]
            p-0
            overflow-hidden
            bg-white
            shadow-2xl
            max-h-[92vh]
            overflow-y-auto
          "
        >
          {/* HEADER */}

          <div
            className="
              bg-gradient-to-r
              from-[#6D3B8C]
              via-[#9B4D96]
              to-[#C17BB2]
              p-6 sm:p-8
              text-white
              relative overflow-hidden
            "
          >
            <div className="absolute inset-0 bg-white/5 backdrop-blur-sm" />

            <DialogHeader className="relative z-10">
              <DialogTitle
                className="
                  text-2xl sm:text-3xl
                  font-bold
                  text-white
                  leading-tight
                "
              >
                Apply — {job.title}
              </DialogTitle>

              <DialogDescription
                className="
                  text-purple-100
                  mt-2
                  text-sm sm:text-base
                "
              >
                {job.department} • {job.location}
              </DialogDescription>
            </DialogHeader>
          </div>

          {/* FORM */}

          <div className="p-5 sm:p-8 bg-gradient-to-b from-white to-purple-50/40">
            <form
              onSubmit={onSubmit}
              className="space-y-4 sm:space-y-5"
            >
              {/* INPUTS */}

              <input
                type="text"
                placeholder="Full Name"
                value={form.name}
                onChange={(e) =>
                  setForm({
                    ...form,
                    name: e.target.value,
                  })
                }
                className="
                  w-full rounded-2xl
                  border border-purple-200
                  bg-white
                  px-5 py-4
                  text-sm sm:text-base
                  outline-none
                  focus:border-purple-500
                  focus:ring-4
                  focus:ring-purple-200/50
                  transition-all
                "
                required
              />

              <input
                type="email"
                placeholder="Email Address"
                value={form.email}
                onChange={(e) =>
                  setForm({
                    ...form,
                    email: e.target.value,
                  })
                }
                className="
                  w-full rounded-2xl
                  border border-purple-200
                  bg-white
                  px-5 py-4
                  text-sm sm:text-base
                  outline-none
                  focus:border-purple-500
                  focus:ring-4
                  focus:ring-purple-200/50
                  transition-all
                "
                required
              />

              <input
                type="text"
                placeholder="Phone Number"
                value={form.phone}
                onChange={(e) =>
                  setForm({
                    ...form,
                    phone: e.target.value,
                  })
                }
                className="
                  w-full rounded-2xl
                  border border-purple-200
                  bg-white
                  px-5 py-4
                  text-sm sm:text-base
                  outline-none
                  focus:border-purple-500
                  focus:ring-4
                  focus:ring-purple-200/50
                  transition-all
                "
              />

              <textarea
                rows="5"
                placeholder="Cover Letter"
                value={form.cover_letter}
                onChange={(e) =>
                  setForm({
                    ...form,
                    cover_letter:
                      e.target.value,
                  })
                }
                className="
                  w-full rounded-2xl
                  border border-purple-200
                  bg-white
                  px-5 py-4
                  text-sm sm:text-base
                  outline-none resize-none
                  focus:border-purple-500
                  focus:ring-4
                  focus:ring-purple-200/50
                  transition-all
                "
              />

              {/* FILE */}

              <label
                className="
                  border-2 border-dashed
                  border-purple-300
                  rounded-3xl
                  p-6 sm:p-8
                  flex flex-col items-center justify-center
                  text-center
                  cursor-pointer
                  hover:border-purple-500
                  hover:bg-purple-50
                  transition-all duration-300
                  bg-white
                "
              >
                <Upload className="w-7 h-7 sm:w-8 sm:h-8 text-purple-600 mb-3" />

                <span className="font-semibold text-slate-800 text-sm sm:text-base">
                  Upload Resume
                </span>

                <span className="text-xs sm:text-sm text-slate-500 mt-1">
                  PDF / DOC / DOCX
                </span>

                <input
                  type="file"
                  className="hidden"
                  onChange={(e) =>
                    setResume(
                      e.target.files[0]
                    )
                  }
                />

                {resume && (
                  <div className="mt-4 text-sm text-purple-700 font-medium break-all">
                    {resume.name}
                  </div>
                )}
              </label>

              {/* BUTTON */}

              <button
                type="submit"
                disabled={submitting}
                className="
                  w-full py-4 rounded-2xl
                  bg-gradient-to-r
                  from-purple-600
                  via-fuchsia-600
                  to-purple-700
                  text-white font-semibold
                  shadow-lg shadow-purple-500/25
                  hover:scale-[1.01]
                  active:scale-[0.98]
                  transition-all duration-300
                  disabled:opacity-60
                  text-sm sm:text-base
                "
              >
                {submitting
                  ? "Submitting..."
                  : "Submit Application"}
              </button>
            </form>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}